import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, RotateCcw } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LoadingStatus } from "../../sessions/models/LoadingStatus";
import { ImportProposal } from "../models/ImportProposal";
import { confirmImport } from "../slice-extensions/assistant-import-slice-extension";
import {
  resetImport,
  selectConfirmStatus,
  selectImportStatusMessage,
} from "../assistant-import-slice";
import { ProposalMembersSection } from "./ProposalMembersSection";
import { ProposalGroupsSection } from "./ProposalGroupsSection";
import { ProposalSessionsSection } from "./ProposalSessionsSection";

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

interface ProposalReviewProps {
  proposal: ImportProposal;
}

export function ProposalReview({ proposal }: ProposalReviewProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const confirmStatus = useAppSelector(selectConfirmStatus);
  const statusMessage = useAppSelector(selectImportStatusMessage);

  const memberNamesByTempId = useMemo(
    () =>
      new Map(
        proposal.members.map((m) => [m.tempId, `${m.firstName} ${m.lastName}`.trim()]),
      ),
    [proposal.members],
  );
  const groupNamesByTempId = useMemo(
    () => new Map(proposal.groups.map((g) => [g.tempId, g.name])),
    [proposal.groups],
  );

  const isConfirming = confirmStatus === LoadingStatus.Loading;
  const isValid =
    (proposal.members.length > 0 || proposal.groups.length > 0 || proposal.sessions.length > 0) &&
    proposal.members.every((m) => m.firstName.trim().length > 0) &&
    proposal.groups.every((g) => g.name.trim().length > 0) &&
    proposal.sessions.every(
      (s) =>
        s.title.trim().length > 0 &&
        s.weekdays.length > 0 &&
        TIME_PATTERN.test(s.timeLocal) &&
        s.durationMinutes >= 5 &&
        s.memberTempIds.length + s.groupTempIds.length > 0,
    );

  return (
    <div className="space-y-4 lg:space-y-6">
      {proposal.warnings.length > 0 && (
        <div className="rounded-lg bg-grey-95 dark:bg-primary-20 p-4">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle size={18} className="shrink-0" />
            {t("assistantImport.review.warningsTitle")}
          </div>
          <ul className="mt-2 list-disc pl-5 text-sm text-grey-30 dark:text-grey-70">
            {proposal.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <ProposalMembersSection members={proposal.members} />
      <ProposalGroupsSection groups={proposal.groups} memberNamesByTempId={memberNamesByTempId} />
      <ProposalSessionsSection
        sessions={proposal.sessions}
        memberNamesByTempId={memberNamesByTempId}
        groupNamesByTempId={groupNamesByTempId}
      />

      {statusMessage && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {statusMessage}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!isValid || isConfirming}
          onClick={() => dispatch(confirmImport(proposal))}
        >
          <Check size={20} strokeWidth={2.5} />
          <span>
            {isConfirming
              ? t("assistantImport.review.creating")
              : t("assistantImport.review.confirm")}
          </span>
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={isConfirming}
          onClick={() => dispatch(resetImport())}
        >
          <RotateCcw size={20} strokeWidth={2} />
          <span>{t("assistantImport.review.startOver")}</span>
        </button>
        {isConfirming && (
          <span
            className="animate-spin rounded-full h-5 w-5 border-2 border-primary-40 border-t-transparent"
            role="status"
            aria-label={t("assistantImport.review.creating")}
          />
        )}
      </div>
    </div>
  );
}
